import { useEffect, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Btn, Field, Spinner } from "@/components/kit";
import { useAdminInvalidate, useAdminQuery } from "@/hooks/queries";
import { adminApi } from "@/lib/api";
import { promoUri, toJpeg } from "@/lib/toto";
import { AdminHeader } from "@/pages/admin/AdminLayout";

const MAX = 5 * 1024 * 1024;

export default function AdminSettings() {
  const { data, isLoading } = useAdminQuery("/admin/settings");
  const refresh = useAdminInvalidate();
  const input = useRef(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data) setF({
      freeScanLimit: String(data.freeScanLimit), price: String(data.premiumPricePaise / 100), duration: String(data.premiumDurationDays), premiumEnabled: data.premiumEnabled,
      adEnabled: data.adBanner?.enabled ?? false, imageUrl: data.adBanner?.imageUrl ?? "", linkUrl: data.adBanner?.linkUrl ?? "", title: data.adBanner?.title ?? "",
    });
  }, [data]);
  if (isLoading || !f) return <Spinner />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.patch("/admin/settings", {
        freeScanLimit: parseInt(f.freeScanLimit, 10) || 0, premiumPricePaise: Math.round(Number(f.price) * 100), premiumDurationDays: parseInt(f.duration, 10) || 365, premiumEnabled: f.premiumEnabled,
        adBanner: { enabled: f.adEnabled, imageUrl: f.imageUrl.trim(), linkUrl: f.linkUrl.trim(), title: f.title.trim() },
      });
      refresh();
      toast.success("Settings saved.");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose a JPG or PNG image.");
    setUploading(true);
    try {
      const blob = file.size > MAX || file.type !== "image/png" ? await toJpeg(file, 1600, 0.85) : file;
      if (blob.size > MAX) throw new Error("Choose an image smaller than 5 MB.");
      const form = new FormData();
      form.append("file", blob, blob === file ? file.name : "banner.jpg");
      const r = await adminApi.upload("/admin/promos/upload", form);
      if (!r?.path) throw new Error("The upload did not return an image. Please try again.");
      setF((x) => ({ ...x, imageUrl: r.path }));
      toast.success("Banner uploaded. Save settings to publish.");
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };
  const preview = promoUri(f.imageUrl);

  return (
    <>
      <AdminHeader title="Plans & Banner" sub="Free-scan limits, Premium pricing and the home promo banner." right={<Btn onClick={save} loading={busy} testid="admin-settings-save">Save settings</Btn>} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="t-card p-6 space-y-4" data-testid="admin-plans-card">
          <h2 className="t-section">Plans & limits</h2>
          <Field label="Free scan limit" type="number" min="0" value={f.freeScanLimit} onChange={set("freeScanLimit")} testid="admin-free-limit" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Premium price (₹)" type="number" min="1" value={f.price} onChange={set("price")} testid="admin-price" />
            <Field label="Duration (days)" type="number" min="1" value={f.duration} onChange={set("duration")} testid="admin-duration" />
          </div>
          <label className="flex items-center justify-between pt-1"><span className="text-[15px] font-semibold">Premium purchases enabled</span><Switch checked={f.premiumEnabled} onCheckedChange={(v) => setF({ ...f, premiumEnabled: v })} data-testid="admin-premium-enabled" /></label>
        </div>
        <div className="t-card p-6 space-y-4" data-testid="admin-banner-card">
          <div className="flex items-center justify-between"><h2 className="t-section">Promo banner</h2><Switch checked={f.adEnabled} onCheckedChange={(v) => setF({ ...f, adEnabled: v })} data-testid="admin-banner-enabled" /></div>
          <button className="w-full rounded-[16px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] aspect-[2.6/1] overflow-hidden flex items-center justify-center hover:border-[#0000EC]/50 transition-colors" onClick={() => input.current.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }} data-testid="admin-banner-upload">
            {uploading ? <Spinner className="py-0" /> : preview ? <img src={preview} alt="Banner" className="w-full h-full object-cover" /> : <span className="flex flex-col items-center gap-2 text-[14px] text-[#64748B]"><ImagePlus className="w-7 h-7 text-[#0000EC]" />Drop an image or click to upload (max 5 MB)</span>}
          </button>
          <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="admin-banner-input" />
          <Field label="Image path or URL" value={f.imageUrl} onChange={set("imageUrl")} testid="admin-banner-image-url" />
          <Field label="Link URL" placeholder="https://" value={f.linkUrl} onChange={set("linkUrl")} testid="admin-banner-link" />
          <Field label="Title" value={f.title} onChange={set("title")} testid="admin-banner-title" />
        </div>
      </div>
    </>
  );
}
